import React, { useMemo } from "react";
import { Search, Users, UserRound, Globe2, ScrollText } from "lucide-react";

import SearchInput from "@/components/shared/SearchInput";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/utils";

/**
 * The top of both record-lookup screens: subject tabs, the search box, and —
 * on the SuperAdmin screen — the all-tenants toggle.
 *
 * The tabs set the `type` query param (`candidate` | `user` | `auto`);
 * `auto` is the SuperAdmin "search everything" case.
 */
const TABS = [
  { type: "candidate", label: "Candidate", Icon: Users },
  { type: "user", label: "User", Icon: UserRound },
  { type: "auto", label: "Everything", Icon: Globe2 },
];

/**
 * SearchPanel component with tab-based search scoping and clear button.
 */
const SearchPanel = ({
  value,
  onValueChange,
  type = "candidate",
  onTypeChange,
  isSuperAdmin = false,
  allTenants = false,
  onAllTenantsChange,
  trailing: Trailing = null,
}) => {
  const tabs = isSuperAdmin ? TABS : TABS.filter((tab) => tab.type !== "auto");

  const placeholder = useMemo(() => {
    switch (type) {
      case "candidate":
        return "Search candidates by name, email, phone or candidate ID…";
      case "user":
        return "Search users/employees by name, email, phone or user ID…";
      case "auto":
        return "Search across candidates and users by name, email, phone or ID…";
      default:
        return "Search by name, email, phone or ID…";
    }
  }, [type]);

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Tabs and tenant controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="What to search for" className="flex items-center gap-1 rounded-full border border-border-primary bg-background-secondary p-1">
          {tabs.map(({ type: tabType, label, Icon }) => {
            const active = type === tabType;

            return (
              <Button
                key={tabType}
                role="tab"
                aria-selected={active}
                onClick={() => onTypeChange(tabType)}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-full px-3.5 text-xs font-medium transition-colors",
                  active
                    ? "bg-tone-blue-soft text-tone-blue ring-1 ring-tone-blue/40"
                    : "bg-background-tertiary text-text-secondary hover:bg-background-icon/40 hover:text-text-primary",
                )}
              >
                <Icon size={13} aria-hidden="true" />
                {label}
              </Button>
            );
          })}
        </div>

        {/* SuperAdmin tenant controls */}
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            {Trailing}

            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-text-secondary">
              <Checkbox checked={allTenants} onCheckedChange={(next) => onAllTenantsChange?.(next === true)} />
              <ScrollText size={12} aria-hidden="true" />
              All tenants
            </label>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="flex w-full items-center gap-2">
        <SearchInput value={value} onChange={onValueChange} placeholder={placeholder} />

        {isSuperAdmin && (
          <span className="hidden items-center gap-1 text-[11px] text-text-muted lg:flex">
            <Search size={12} aria-hidden="true" />
            Matches across every tenant, as far as your scope allows
          </span>
        )}
      </div>
    </div>
  );
};

export default SearchPanel;
